import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Constants } from "@app/constants";
import { AuthenticationService, getContext } from "@app/core";
import { environment } from "@env/environment";
import { time } from "highcharts";
import { catchError, firstValueFrom, map, retry, throwError, timeout } from "rxjs";

@Injectable({ providedIn: 'root' })
export class IbexTelephonyService {
    constructor(private http: HttpClient, private authService: AuthenticationService) {
        
    }
    ibexLogin(campaigns: any) {
        return this.http.disableLoader().post(
            Constants.APIS.TELEPHONY.IBEX_DIALER.LOGIN,
            {
                "campaigns": campaigns
            },
            { context: getContext('telephony') }
        );
    }
    async ibexLogout() {
        const token = await firstValueFrom(this.authService.getCurrentUserToken());
        return fetch(`${environment.telephony.url}${environment.telephony.server}${Constants.APIS.TELEPHONY.IBEX_DIALER.LOGOUT}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ "isForce": true }),
            keepalive: true,
        }).then((response) => response.text()).catch((error) => console.error('Error:', error));
    }
    addUserStatusDB(data: any) {
        return this.http.disableLoader().post<any>(Constants.APIS.USER.STATUS_LOG, data);
    }
    updateIbexStatus(data: any) {
        return this.http.disableErrorHandler().disableLoader().post(
            Constants.APIS.TELEPHONY.IBEX_DIALER.UPDATE_BREAK_STATUS,
            data,
            { context: getContext('telephony'), headers: new HttpHeaders({ timeout: 15000 }) }
        );
    }
    endCall(data: any) {
        return this.http.disableLoader().post(
            Constants.APIS.TELEPHONY.IBEX_DIALER.END_CALL,
            data,
            { context: getContext('telephony'), headers: new HttpHeaders({ timeout: 30000 }) }
        );
    }
    loginStatus() {
        return this.http.disableLoader().get(
            Constants.APIS.TELEPHONY.IBEX_DIALER.LOGIN_STATUS,
            { context: getContext('telephony') }
        );
    }
    hold(onHold: boolean, callSid: string) {
        return this.http.post(
            Constants.APIS.TELEPHONY.HOLD,
            {
                Who: 0,
                IsConferenceConsultancy: false,
                Hold: onHold,
                CustomerCallSid: callSid
            },
            { context: getContext('telephony') }
        );
    }
    manualCall(data: any) {
        return this.http.post(
            Constants.APIS.TELEPHONY.IBEX_DIALER.MANUAL_CALL,
            data,
            { context: getContext('telephony') }
        );
    }
    consult(data: any) {
        return this.http.post(
            Constants.APIS.TELEPHONY.CONSULT,
            data,
            { context: getContext('telephony') }
        );
    }
    conference(data: any) {
        return this.http.post(
            Constants.APIS.TELEPHONY.CONFERENCE,
            data,
            { context: getContext('telephony') }
        );
    }
    endThirdParty(data: any) {
        return this.http.post(
            Constants.APIS.TELEPHONY.ENDTHIRDPARTY,
            data,
            { context: getContext('telephony') }
        );
    }
    toggle(data: any) {
        return this.http.post(
            Constants.APIS.TELEPHONY.TOGGLE,
            data,
            { context: getContext('telephony') }
        );
    }
    transferCall(data: any) {
        return this.http.post(
            Constants.APIS.TELEPHONY.IBEX_DIALER.TRANSFER_CALL,
            data,
            { context: getContext('telephony') }
        );
    }
    getQueuedCalls(program: string) {
        const url = `${Constants.APIS.TELEPHONY.IBEX_DIALER.GET_QUEUED_CALLS}`;
        return this.http
            .disableLoader()
            .get(url, { context: getContext('telephony') })
            .pipe(
                map((data: any) => {
                    return {
                        count: data.length,
                        leads: data.map((d: any) => {
                            return {
                                campaign: d.C0,
                                callSid: d.C1,
                                duration: (new Date().getTime() - new Date(d.C15).getTime()) / 1000,
                                to: d.C4,
                                from: d.C3,
                                callDirection: d.C16,
                            };
                        }),
                    };
                })
            );
    }
    getOutboundQueuedCalls(accountSid: string, campaigns: string, direction: number, selectedDialingCountry: string | null) {
        const url = `${Constants.APIS.TELEPHONY.QUEUED_CALLS}?AccountSid=${accountSid}&Campaigns=${campaigns}&Direction=${direction}&CountryCode=${selectedDialingCountry}`;
        return this.http
            .disableLoader()
            .get(url, { context: getContext('telephony') })
            .pipe(
                map((data: any) => {
                    return {
                        count: data.Count,
                        leads: data.Leads.map((d: any) => {
                            return {
                                leadId: d.LeadId,
                                email: d.Email,
                                campaign: d.Campaign,
                                callSid: d.CallSid,
                                duration: d.Duration,
                                to: d.To,
                                from: d.From,
                                callDirection: d.CallDirection,
                                toCountry: d.ToCountry,
                                fromCountry: d.FromCountry,
                                taskSid: d.TaskSid,
                                dialedCount: d.DialedCount,
                            };
                        }),
                    };
                })
            );
    }
    updateCallStatus(data: any) {
        return this.http.disableLoader().post(
            Constants.APIS.TELEPHONY.UPDATE_STATUS,
            data,
            { context: getContext('telephony') }
        );
    }
    loggedInInsightUser() {
        return this.http.post(
            Constants.APIS.TELEPHONY.IBEX_DIALER.INSIGHTS_LOGIN_USER,
            {},
            { context: getContext('telephony') }
        );
    }
    async forceDisposeAndHangupCall(
        call_sid: string,
        userId: string,
        lead: any
    ): Promise<boolean> {
        try {
            const token = await firstValueFrom(this.authService.getCurrentUserToken());

            // Fire-and-forget first request
            fetch(
                `${environment.api.url}${environment.api.server}${Constants.APIS.TELEPHONY.FORCE_DISPOSED}`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ CallSid: call_sid, UserId: userId }),
                    keepalive: true,
                }
            );

            // Await second request and return based on success
            const response = await fetch(
                `${environment.telephony.url}${environment.telephony.server}${Constants.APIS.TELEPHONY.IBEX_DIALER.END_CALL}`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        CallSid: call_sid,
                        TermCode: lead.termcd,
                        Lead: lead,
                    }),
                    keepalive: true,
                }
            );

            return response.ok;
        } catch (err) {
            console.error('Error in forceDisposeAndHangupCall:', err);
            return false;
        }
    }
    getAgentCurrentStatus() {
        return this.http.disableLoader().get(
            Constants.APIS.TELEPHONY.IBEX_DIALER.CURRENT_STATUS,
            { context: getContext('telephony'), headers: new HttpHeaders({ timeout: 20000 }) }
        );
    }
    markCallComplete() {
        return this.http.disableLoader().post(
            Constants.APIS.TELEPHONY.MARK_COMPLETE,
            {}
        );
    }
    getActiveCalls() {
        return this.http.get(
            Constants.APIS.TELEPHONY.IBEX_DIALER.ACTIVE_CALLS,
            { context: getContext('telephony') }
        );
    }
}
